import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { fetchWeeks, fetchSummary, fetchResponses, exportCsv, exportXlsx } from "../api";
import Loading from "../components/Loading";
import ErrorRetry from "../components/ErrorRetry";
import CreateTaskFromBlockerButton from "../components/tasks/CreateTaskFromBlockerButton";
import { formatWeekDate } from "../utils/formatWeek";

const METRIC_LABELS = {
  score_workload: "Нагрузка",
  score_deadlines: "Дедлайны",
  score_communication: "Коммуникация",
  score_energy: "Энергия",
};

// Как в Командном центре: низкий балл плох для «хороших» метрик;
// для нагрузки риск — перегруз (≥ 4).
const LOW_SCORE = 2;
const HIGH_WORKLOAD = 4;

function scoreTone(metricKey, value) {
  if (value == null) return "neutral";
  if (metricKey === "score_workload") {
    if (value >= HIGH_WORKLOAD) return "low";
    if (value <= LOW_SCORE) return "high";
    return "medium";
  }
  if (value <= LOW_SCORE) return "low";
  if (value === 3) return "medium";
  return "high";
}

function avgTone(metricKey, value) {
  if (value == null) return undefined;
  return scoreTone(metricKey, Math.round(value));
}

function clientBlockersOf(response) {
  return (response.client_responses || []).filter(
    (cr) => cr.has_blockers && cr.blocker_text && String(cr.blocker_text).trim()
  );
}

function workloadCommentsOf(response) {
  return (response.client_responses || []).filter(
    (cr) => cr.workload_comment && String(cr.workload_comment).trim()
  );
}

function needsAttention(response) {
  if (response.score_workload != null && response.score_workload >= HIGH_WORKLOAD) {
    return true;
  }
  if (response.score_deadlines != null && response.score_deadlines <= LOW_SCORE) {
    return true;
  }
  if (response.score_communication != null && response.score_communication <= LOW_SCORE) {
    return true;
  }
  if (response.score_energy != null && response.score_energy <= LOW_SCORE) {
    return true;
  }
  if (response.has_blockers) return true;
  if (clientBlockersOf(response).length > 0) return true;
  if (
    (response.client_responses || []).some(
      (cr) => cr.score_workload != null && cr.score_workload >= HIGH_WORKLOAD
    )
  ) {
    return true;
  }
  return false;
}

function formatAvg(value) {
  if (value == null) return "—";
  return Number(value).toFixed(1);
}

function TextSection({ title, count, children }) {
  if (!children) return null;
  return (
    <div className="team-week-text-section">
      <div className="team-week-text-section-title">
        {title}
        {count != null && <span className="team-week-text-section-count">{count}</span>}
      </div>
      {children}
    </div>
  );
}

function WorkloadReasonsTable({ reasons }) {
  const rows = useMemo(() => {
    return [...reasons].sort((a, b) => {
      const scoreA = a.score == null ? -1 : a.score;
      const scoreB = b.score == null ? -1 : b.score;
      if (scoreB !== scoreA) return scoreB - scoreA;
      const emp = (a.employee || "").localeCompare(b.employee || "", "ru");
      if (emp !== 0) return emp;
      return (a.client_name || "").localeCompare(b.client_name || "", "ru");
    });
  }, [reasons]);

  return (
    <div className="table-container team-week-workload-table-wrap">
      <table className="score-table team-week-workload-table">
        <thead>
          <tr>
            <th>Сотрудник</th>
            <th>Клиент</th>
            <th>Балл</th>
            <th>Причина</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => (
            <tr key={`wl-${w.employee}-${w.client_name}-${w.score}-${w.text}`}>
              <td className="team-week-workload-emp">{w.employee}</td>
              <td>{w.client_name}</td>
              <td>
                <span className={`score-badge ${scoreTone("score_workload", w.score)}`}>
                  {w.score ?? "—"}
                </span>
              </td>
              <td className="cell-text">{w.text}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BlockersCell({ response }) {
  const empBlocker =
    response.has_blockers && response.blocker_text
      ? String(response.blocker_text).trim()
      : "";
  const clientBs = clientBlockersOf(response);

  if (!empBlocker && clientBs.length === 0) return "—";

  return (
    <div className="team-week-blockers-cell">
      {empBlocker ? <span className="cell-alert">{empBlocker}</span> : null}
      {clientBs.map((cr) => (
        <div key={cr.id} className="cell-alert team-week-client-blocker">
          <span className="team-week-client-blocker-name">{cr.client_name}: </span>
          {cr.blocker_text}
        </div>
      ))}
    </div>
  );
}

export default function TeamWeek() {
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState("");
  const [summary, setSummary] = useState(null);
  const [responses, setResponses] = useState([]);
  const [filter, setFilter] = useState("all");
  const [weeksError, setWeeksError] = useState(null);
  const [dataError, setDataError] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [lastExportType, setLastExportType] = useState("csv");
  const requestIdRef = useRef(0);

  const loadWeeks = useCallback(() => {
    setWeeksError(null);
    fetchWeeks()
      .then((data) => {
        setWeeks(data);
        if (data.length > 0) {
          setSelectedWeek((prev) => prev || data[0]);
        }
      })
      .catch((err) => {
        console.error("Failed to load weeks:", err);
        setWeeksError("Не удалось загрузить список недель");
      });
  }, []);

  useEffect(() => {
    loadWeeks();
  }, [loadWeeks]);

  const loadWeekData = useCallback(() => {
    if (!selectedWeek) return;
    setDataError(null);
    setDataLoading(true);
    const requestId = ++requestIdRef.current;
    Promise.all([fetchSummary(selectedWeek), fetchResponses(selectedWeek)])
      .then(([sum, res]) => {
        if (requestId !== requestIdRef.current) return;
        setSummary(sum);
        setResponses(res);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error("Failed to load data:", err);
        setDataError("Не удалось загрузить данные");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setDataLoading(false);
      });
  }, [selectedWeek]);

  useEffect(() => {
    loadWeekData();
  }, [loadWeekData]);

  const completed = useMemo(
    () => responses.filter((r) => r.completed_at),
    [responses]
  );
  const waiting = useMemo(
    () => responses.filter((r) => !r.completed_at),
    [responses]
  );
  const attention = useMemo(
    () => completed.filter(needsAttention),
    [completed]
  );

  const clientBlockers = useMemo(() => {
    const items = [];
    for (const r of completed) {
      for (const cr of clientBlockersOf(r)) {
        items.push({
          key: `cb-${cr.id}`,
          employee: r.employee_name,
          client_name: cr.client_name,
          text: cr.blocker_text,
          response_id: r.id,
          client_response_id: cr.id,
        });
      }
    }
    return items;
  }, [completed]);

  const visibleResponses = useMemo(() => {
    if (filter === "attention") return attention;
    if (filter === "waiting") return waiting;
    return responses;
  }, [filter, responses, attention, waiting]);

  const handleExport = async (type) => {
    setExportError(null);
    setLastExportType(type);
    setExporting(type);
    try {
      if (type === "csv") {
        await exportCsv(selectedWeek);
      } else {
        await exportXlsx(selectedWeek);
      }
    } catch (err) {
      console.error("Export failed:", err);
      setExportError("Не удалось экспортировать данные");
    } finally {
      setExporting(null);
    }
  };

  const weekIndex = weeks.indexOf(selectedWeek);
  const goWeek = (delta) => {
    if (weekIndex < 0) return;
    const next = weeks[weekIndex + delta];
    if (next) setSelectedWeek(next);
  };

  const hasOpenTexts =
    (summary?.blockers?.length || 0) > 0 ||
    (summary?.comments?.length || 0) > 0 ||
    (summary?.communication_reasons?.length || 0) > 0 ||
    (summary?.workload_reasons?.length || 0) > 0 ||
    clientBlockers.length > 0;

  if (weeksError) {
    return <ErrorRetry message={weeksError} onRetry={loadWeeks} />;
  }

  if (weeks.length === 0 && !selectedWeek) {
    return <div className="empty-state">Нет данных по неделям</div>;
  }

  if (!summary && dataLoading) {
    return <Loading text="Загрузка данных команды..." />;
  }

  const isRefreshing = dataLoading && Boolean(summary);

  return (
    <div className={`team-week${isRefreshing ? " is-refreshing" : ""}`}>
      <div className="section-header">
        <div>
          <h2>Команда за неделю</h2>
          <div className="section-subtitle">
            Сводка ответов, рисков и комментариев за выбранную неделю
          </div>
        </div>
        <div className="controls">
          {isRefreshing && (
            <span className="team-week-refresh-hint" aria-live="polite">
              Обновление…
            </span>
          )}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => goWeek(1)}
            disabled={weekIndex < 0 || weekIndex >= weeks.length - 1 || isRefreshing}
            title="Более ранняя неделя"
          >
            ←
          </button>
          <select
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(e.target.value)}
            disabled={isRefreshing}
          >
            {weeks.map((w) => (
              <option key={w} value={w}>
                Неделя с {formatWeekDate(w)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => goWeek(-1)}
            disabled={weekIndex <= 0 || isRefreshing}
            title="Более поздняя неделя"
          >
            →
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => handleExport("csv")}
            disabled={!selectedWeek || exporting !== null}
          >
            {exporting === "csv" ? "..." : "CSV"}
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => handleExport("xlsx")}
            disabled={!selectedWeek || exporting !== null}
          >
            {exporting === "xlsx" ? "..." : "Excel"}
          </button>
        </div>
      </div>

      {exportError && (
        <ErrorRetry
          message={exportError}
          onRetry={() => handleExport(lastExportType)}
        />
      )}
      {dataError && <ErrorRetry message={dataError} onRetry={loadWeekData} />}

      {summary && (
        <>
          <div className="stats-bar">
            <div className="stat-card">
              <span className="stat-value">
                {summary.completed_surveys}/{summary.total_employees}
              </span>
              <span className="stat-label">
                Прошли опрос ({summary.completion_rate}%)
              </span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{attention.length}</span>
              <span className="stat-label">Требуют внимания</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{waiting.length}</span>
              <span className="stat-label">Ожидают прохождения</span>
            </div>
            <div className="stat-card">
              <span
                className="stat-value"
                style={{ color: `var(--score-${avgTone("score_workload", summary.avg_workload) || "neutral"})` }}
              >
                {formatAvg(summary.avg_workload)}
              </span>
              <span className="stat-label">Ср. нагрузка</span>
            </div>
            <div className="stat-card">
              <span
                className="stat-value"
                style={{ color: `var(--score-${avgTone("score_deadlines", summary.avg_deadlines) || "neutral"})` }}
              >
                {formatAvg(summary.avg_deadlines)}
              </span>
              <span className="stat-label">Ср. дедлайны</span>
            </div>
            <div className="stat-card">
              <span
                className="stat-value"
                style={{ color: `var(--score-${avgTone("score_communication", summary.avg_communication) || "neutral"})` }}
              >
                {formatAvg(summary.avg_communication)}
              </span>
              <span className="stat-label">Ср. коммуникация</span>
            </div>
            <div className="stat-card">
              <span
                className="stat-value"
                style={{ color: `var(--score-${avgTone("score_energy", summary.avg_energy) || "neutral"})` }}
              >
                {formatAvg(summary.avg_energy)}
              </span>
              <span className="stat-label">Ср. энергия</span>
            </div>
          </div>

          <div className="data-card">
            <div className="data-card-header">
              <div className="data-card-title">Ответы сотрудников</div>
              <div className="toolbar-segment">
                <button
                  type="button"
                  className={filter === "all" ? "active" : ""}
                  onClick={() => setFilter("all")}
                >
                  Все
                </button>
                <button
                  type="button"
                  className={filter === "attention" ? "active" : ""}
                  onClick={() => setFilter("attention")}
                >
                  Требуют внимания
                </button>
                <button
                  type="button"
                  className={filter === "waiting" ? "active" : ""}
                  onClick={() => setFilter("waiting")}
                >
                  Ожидают
                </button>
              </div>
            </div>
            <div className="data-card-body" style={{ padding: 0 }}>
              {visibleResponses.length === 0 ? (
                <div className="empty-state">Нет записей для выбранного фильтра</div>
              ) : (
                <div className="table-container">
                  <table className="score-table">
                    <thead>
                      <tr>
                        <th>Сотрудник</th>
                        {Object.values(METRIC_LABELS).map((label) => (
                          <th key={label}>{label}</th>
                        ))}
                        <th>Блокеры</th>
                        <th>Комментарий</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleResponses.map((r) => {
                        const wlComments = workloadCommentsOf(r);
                        const wlTitle =
                          wlComments.length > 0
                            ? wlComments
                                .map((cr) => `${cr.client_name}: ${cr.workload_comment}`)
                                .join("\n")
                            : undefined;
                        return (
                          <tr
                            key={r.id}
                            className={
                              r.completed_at && needsAttention(r)
                                ? "team-week-row--attention"
                                : undefined
                            }
                          >
                            <td>
                              <div className="employee-cell">
                                <div>
                                  <div className="employee-name">{r.employee_name}</div>
                                  {r.has_midweek_updates && (
                                    <span className="inline-status changed">
                                      Обновлял клиента
                                    </span>
                                  )}
                                  {!r.completed_at && (
                                    <span className="inline-status">Не завершен</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            {Object.keys(METRIC_LABELS).map((key) => {
                              const commentForKey =
                                key === "score_communication"
                                  ? r.communication_comment
                                  : key === "score_workload"
                                  ? wlTitle
                                  : null;
                              const tone = scoreTone(key, r[key]);
                              return (
                                <td
                                  key={key}
                                  title={
                                    commentForKey
                                      ? `Причина: ${commentForKey}`
                                      : undefined
                                  }
                                >
                                  <span className={`score-badge ${tone}`}>
                                    {r[key] ?? "-"}
                                  </span>
                                  {commentForKey ? (
                                    <sup className="team-week-note-mark">✎</sup>
                                  ) : null}
                                </td>
                              );
                            })}
                            <td className="cell-text">
                              <BlockersCell response={r} />
                            </td>
                            <td className="cell-text">
                              {r.has_comment ? r.comment_text : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {hasOpenTexts && (
            <div className="data-card">
              <div className="data-card-header">
                <div className="data-card-title">Открытые тексты</div>
              </div>
              <div className="data-card-body">
                <TextSection
                  title="Коммуникация"
                  count={summary.communication_reasons?.length || 0}
                >
                  {(summary.communication_reasons?.length || 0) > 0 ? (
                    <div className="comments-grid">
                      {summary.communication_reasons.map((c) => (
                        <div
                          className="comment-card"
                          key={`comm-${c.employee}-${c.score}-${c.text}`}
                        >
                          <div className="comment-header">
                            <span className="comment-author">
                              {c.employee} · коммуникация {c.score}
                            </span>
                          </div>
                          <div className="comment-text">{c.text}</div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </TextSection>

                <TextSection
                  title="Нагрузка по клиентам"
                  count={summary.workload_reasons?.length || 0}
                >
                  {(summary.workload_reasons?.length || 0) > 0 ? (
                    <WorkloadReasonsTable reasons={summary.workload_reasons} />
                  ) : null}
                </TextSection>

                <TextSection title="Блокеры сотрудников" count={summary.blockers.length}>
                  {summary.blockers.length > 0 ? (
                    <div className="blockers-grid">
                      {summary.blockers.map((b) => (
                        <div className="blocker-card" key={`b-${b.response_id}`}>
                          <div className="blocker-header">
                            <span className="blocker-author">{b.employee}</span>
                            <CreateTaskFromBlockerButton
                              sourceResponseId={b.response_id}
                            />
                          </div>
                          <div className="blocker-text">{b.text}</div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </TextSection>

                <TextSection title="Блокеры по клиентам" count={clientBlockers.length}>
                  {clientBlockers.length > 0 ? (
                    <div className="blockers-grid">
                      {clientBlockers.map((b) => (
                        <div className="blocker-card" key={b.key}>
                          <div className="blocker-header">
                            <span className="blocker-author">
                              {b.employee} → {b.client_name}
                            </span>
                            <CreateTaskFromBlockerButton
                              sourceResponseId={b.response_id}
                              sourceClientResponseId={b.client_response_id}
                            />
                          </div>
                          <div className="blocker-text">{b.text}</div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </TextSection>

                <TextSection title="Комментарии" count={summary.comments.length}>
                  {summary.comments.length > 0 ? (
                    <div className="comments-grid">
                      {summary.comments.map((c) => (
                        <div
                          className="comment-card"
                          key={`c-${c.employee}-${c.text}`}
                        >
                          <div className="comment-header">
                            <span className="comment-author">{c.employee}</span>
                          </div>
                          <div className="comment-text">{c.text}</div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </TextSection>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
